import { Option } from "@/constants/common";
import { MiniCogValue } from "./initial-f2f-types";

export const MINI_COG_WORD_VERSIONS: string[][] = [
  ["Banana", "Sunrise", "Chair"],
  ["Leader", "Season", "Table"],
  ["Village", "Kitchen", "Baby"],
  ["River", "Nation", "Finger"],
  ["Captain", "Garden", "Picture"],
  ["Daughter", "Heaven", "Mountain"],
];

export const MINI_COG_VERSION_LABEL = "Version";

export const MINI_COG_TEXT = {
  step1Title: "Step 1: Three Word Registration",
  step1Instruction:
    "Look directly at person and say, “Please listen carefully. I am going to say three words that I want you to repeat back to me now and try to remember. The words are [select a list of words from the versions below]. Please say them for me now.” If the person is unable to repeat the words after three attempts, move on to Step 2 (clock drawing).",
  step1Note:
    "The following and other word lists have been used in one or more clinical studies.",
  step1NoteSuperscript: "1-3",
  step1NoteTail:
    " For repeated administration, use of an alternative word list is recommended",
  step2Title: "Step 2: Clock Drawing",
  step2Instruction:
    "Say: “Next, I want you to draw a clock for me. First, put in all of the numbers where they go.” When that is completed, say: “Now, set the hands to 10 past 11.”",
  clockImageLabel: "Clock drawing",
  uploadImage: "Upload image",
  clearImage: "clear",
  uploadHelper: "Upload an image of the clock drawing (.jpg, .jpeg, .png)",
  clockImageAlt: "Clock drawing",
  imageTooLarge: "Choose an image under 2 MB.",
  imageTypeError: "Only .jpg, .jpeg and .png files are allowed.",
  step3Title: "Step 3: Three Word Recall",
  step3Instruction:
    "Ask the person to recall the three words you stated in Step 1. Say: “What were the three words I asked you to remember?” Record the word list version number and the person’s answers below.",
  wordListVersionLabel: "Word List Version",
  wordListVersionPlaceholder: "Select",
  personsAnswersLabel: "Person’s Answers",
  scoringTitle: "SCORING",
  wordRecallLabel: "Word Recall:",
  wordRecallRange: "(0-3 points)",
  wordRecallDescription:
    "1 point for each word spontaneously recalled without cueing.",
  clockDrawLabel: "Clock Draw:",
  clockDrawRange: "(0 or 2 points)",
  clockDrawDescription:
    "Normal clock = 2 points. A normal clock has all numbers placed in the correct sequence and approximately correct position (e.g., 12, 3, 6 and 9 are in anchor positions) with no missing or duplicate numbers. Hands are pointing to the 11 and 2 (11:10). Hand length is not scored. Inability or refusal to draw a clock (abnormal) = 0 points.",
  totalScoreLabel: "Total Score:",
  totalScoreRange: "(0-5 points)",
  totalScoreEmpty: "–",
  totalScoreDescription:
    "Total score = Word Recall score + Clock Draw score. A cut point of <3 on the Mini-Cog™ has been validated for dementia screening, but many individuals with clinically meaningful cognitive impairment will score higher. When greater sensitivity is desired, a cut point of <4 is recommended as it may indicate a need for further evaluation of cognitive status.",
} as const;

export const MINI_COG_SCORE_PLACEHOLDER = "–";
export const MINI_COG_WORD_RECALL_OPTIONS: Option[] = ["0", "1", "2", "3"].map(
  (value) => ({ value, label: value })
);
export const MINI_COG_CLOCK_DRAW_OPTIONS: Option[] = ["0", "2"].map(
  (value) => ({ value, label: value })
);

export const MINI_COG_MAX = { wordRecall: 3, clockDraw: 2, total: 5 } as const;
export const MINI_COG_POSITIVE_CUT_POINT = 3;
export const MINI_COG_SENSITIVE_CUT_POINT = 4;

export const MINI_COG_INTERPRETATION = {
  positive: "positive screen for dementia (below 3)",
  borderline:
    "negative at the standard cut point; below 4, further evaluation of cognitive status may be needed",
  negative: "negative screen",
} as const;

export const MINI_COG_SUMMARY_LABELS = {
  registration: "Three word registration",
  clockImage: "Clock drawing",
  clockImageAttached: "image attached",
  wordListVersion: "Word list version",
  personsAnswers: "Person's answers",
  wordRecall: "Word recall",
  clockDraw: "Clock draw",
  total: "Mini-Cog total score",
} as const;

export const MINI_COG_ANSWER_KEYS = {
  registrationWords: "registration_words",
  clockImage: "clock_drawing",
  wordListVersion: "word_list_version",
  personsAnswers: "persons_answers",
  wordRecallScore: "word_recall_score",
  clockDrawScore: "clock_draw_score",
  totalScore: "total_score",
} as const;

export const MINI_COG_IMAGE_ACCEPT = ".jpg,.jpeg,.png";
export const MINI_COG_IMAGE_MIME_TYPES = ["image/jpeg", "image/png"];
export const MINI_COG_IMAGE_MAX_BYTES = 2 * 1024 * 1024;
export const MINI_COG_IMAGE_BOX_WIDTH_PX = 600;
export const MINI_COG_IMAGE_BOX_HEIGHT_PX = 330;
export const MINI_COG_PANEL_BACKGROUND = "#1A1A1A0D";
export const MINI_COG_BORDER_COLOR = "#E0E0E0";
export const MINI_COG_VERSION_COLUMNS = 6;

export const MINI_COG_DEFAULT_VALUE: MiniCogValue = {
  registrationWords: [],
  clockImage: "",
  wordListVersion: "",
  personsAnswers: "",
  wordRecallScore: "",
  clockDrawScore: "",
};
